<template>
  <div class="kv-ve__group">
    <div class="form-group">
      <label>{{ $_("visualEditor-emailEditor-common-text") }}</label>
      <input class="form-control" v-model="block.settings.text" />
      <small class="text-muted d-block mt-1">
        {{ $_("visualEditor-emailEditor-button-canvasHint") }}
      </small>
    </div>
    <div class="form-group">
      <label>{{ $_("visualEditor-emailEditor-common-link") }}</label>
      <input
        class="form-control"
        v-model="block.settings.href"
        :placeholder="$_('visualEditor-emailEditor-common-urlPlaceholder')"
      />
    </div>
    <div class="form-group">
      <label>{{ $_("visualEditor-emailEditor-common-align") }}</label>
      <select class="form-control" v-model="block.settings.align">
        <option value="left">{{ $_("visualEditor-emailEditor-common-left") }}</option>
        <option value="center">{{ $_("visualEditor-emailEditor-common-center") }}</option>
        <option value="right">{{ $_("visualEditor-emailEditor-common-right") }}</option>
      </select>
    </div>
    <div class="form-group">
      <label>{{ $_("visualEditor-emailEditor-common-width") }}</label>
      <select class="form-control" v-model="block.settings.width">
        <option value="auto">{{ $_("visualEditor-emailEditor-common-widthAuto") }}</option>
        <option value="filled">{{ $_("visualEditor-emailEditor-common-widthFilled") }}</option>
      </select>
    </div>

    <div class="form-row">
      <div class="form-group col-6">
        <label>{{ $_("visualEditor-emailEditor-common-background") }}</label>
        <VeColorPicker v-model="block.settings.backgroundColor" />
      </div>
      <div class="form-group col-6">
        <label>{{ $_("visualEditor-emailEditor-common-text") }}</label>
        <VeColorPicker v-model="block.settings.color" />
      </div>
    </div>

    <div class="form-row">
      <div class="form-group col-12">
        <label>{{ $_("visualEditor-emailEditor-common-corners") }}</label>
        <VeCornersPicker
          v-model="block.settings.borderRadius"
          :options="borderRadiusOptions.map((o) => ({ value: o.value, title: $_(o.labelKey) }))"
        />
      </div>
    </div>

    <div class="form-group">
      <VeBorderSettings
        v-model="borderProxy"
        :label="$_('visualEditor-emailEditor-borderSettings-label')"
      />
    </div>
    <div class="form-row">
      <div class="form-group col-12">
        <label>{{ $_("visualEditor-emailEditor-common-padding") }}</label>
        <VeSpacingPicker
          v-model="paddingProxy"
          :y-label="$_('visualEditor-emailEditor-common-axisY')"
          :x-label="$_('visualEditor-emailEditor-common-axisX')"
          :centerLabel="$_('visualEditor-emailEditor-common-padding')"
          :min="0"
          :max="120"
          :step="1"
        />
      </div>
    </div>

    <div class="form-row">
      <div class="form-group col-12">
        <label>{{ $_("visualEditor-emailEditor-common-margin") }}</label>
        <VeSpacingPicker
          v-model="marginProxy"
          :y-label="$_('visualEditor-emailEditor-common-axisY')"
          :x-label="$_('visualEditor-emailEditor-common-axisX')"
          :centerLabel="$_('visualEditor-emailEditor-common-margin')"
          :min="0"
          :max="200"
          :step="1"
        />
      </div>
    </div>
  </div>
</template>

<script>
import KvTypography from "@/components/ui/kv-typography.vue";
import VeCornersPicker from "../../components/VeCornersPicker.vue";
import VeSpacingPicker from "../../components/VeSpacingPicker.vue";
import VeColorPicker from "../../components/VeColorPicker.vue";
import VeBorderSettings from "../../components/VeBorderSettings.vue";

export default {
  name: "VeButtonBlockSettings",
  components: { KvTypography, VeCornersPicker, VeSpacingPicker, VeColorPicker, VeBorderSettings },
  props: {
    block: { type: Object, required: true },
    paletteLabel: { type: String, required: true },
    borderRadiusOptions: { type: Array, required: true },
  },
  computed: {
    paddingProxy: {
      get() {
        const px = Number(this.block?.settings?.paddingX ?? 18);
        const py = Number(this.block?.settings?.paddingY ?? 12);
        const safeX = Number.isFinite(px) && px >= 0 ? Math.round(px) : 18;
        const safeY = Number.isFinite(py) && py >= 0 ? Math.round(py) : 12;
        return { top: safeY, right: safeX, bottom: safeY, left: safeX };
      },
      set(next) {
        const v = next || {};
        const top = Number(v.top);
        const right = Number(v.right);
        const bottom = Number(v.bottom);
        const left = Number(v.left);
        const safeX = Number.isFinite(right) ? right : (Number.isFinite(left) ? left : 18);
        const safeY = Number.isFinite(top) ? top : (Number.isFinite(bottom) ? bottom : 12);
        if (!this.block.settings) this.$set(this.block, "settings", {});
        this.$set(this.block.settings, "paddingX", Math.max(0, Math.round(safeX)));
        this.$set(this.block.settings, "paddingY", Math.max(0, Math.round(safeY)));
      },
    },
    marginProxy: {
      get() {
        const mt = Number(this.block?.settings?.marginTop ?? 0);
        const mr = Number(this.block?.settings?.marginRight ?? 0);
        const mb = Number(this.block?.settings?.marginBottom ?? 0);
        const ml = Number(this.block?.settings?.marginLeft ?? 0);
        const safe = (n) => (Number.isFinite(n) && n >= 0 ? Math.round(n) : 0);
        return { top: safe(mt), right: safe(mr), bottom: safe(mb), left: safe(ml) };
      },
      set(next) {
        const v = next || {};
        const safe = (n) => (Number.isFinite(n) ? Math.max(0, Math.round(n)) : 0);
        if (!this.block.settings) this.$set(this.block, "settings", {});
        this.$set(this.block.settings, "marginTop", safe(Number(v.top)));
        this.$set(this.block.settings, "marginRight", safe(Number(v.right)));
        this.$set(this.block.settings, "marginBottom", safe(Number(v.bottom)));
        this.$set(this.block.settings, "marginLeft", safe(Number(v.left)));
      },
    },
    borderProxy: {
      get() {
        const b = this.block?.settings?.border || {};
        const sides = b.sides || {};
        return {
          enabled: Boolean(b.enabled),
          style: b.style || "solid",
          color: b.color || "#e5e7eb",
          width: Number.isFinite(Number(b.width)) ? Math.max(1, Math.min(20, Math.round(Number(b.width)))) : 1,
          sides: {
            top: sides.top !== false,
            right: sides.right !== false,
            bottom: sides.bottom !== false,
            left: sides.left !== false,
          },
        };
      },
      set(next) {
        const v = next || {};
        if (!this.block.settings) this.$set(this.block, "settings", {});
        if (!this.block.settings.border) this.$set(this.block.settings, "border", {});
        this.$set(this.block.settings.border, "enabled", Boolean(v.enabled));
        this.$set(this.block.settings.border, "style", String(v.style || "solid").toLowerCase());
        this.$set(this.block.settings.border, "color", typeof v.color === "string" && v.color ? v.color : "#e5e7eb");
        const w = Number(v.width);
        const width = Number.isFinite(w) ? Math.max(1, Math.min(20, Math.round(w))) : 1;
        this.$set(this.block.settings.border, "width", width);
        const sides = v.sides || {};
        this.$set(this.block.settings.border, "sides", {
          top: sides.top !== false,
          right: sides.right !== false,
          bottom: sides.bottom !== false,
          left: sides.left !== false,
        });
      },
    },
  },
  methods: {
  },
};
</script>


